import { AgentApiError } from "../api/agent-conversation-client"
import type { RobinFileAttachment } from "../robin-types"

export function isImageAttachment(attachment: RobinFileAttachment): boolean {
  return attachment.mimeType.startsWith("image/")
}

export function isPdfAttachment(attachment: RobinFileAttachment): boolean {
  return (
    attachment.mimeType === "application/pdf" ||
    attachment.fileName.toLowerCase().endsWith(".pdf")
  )
}

export function isPreviewableAttachment(attachment: RobinFileAttachment): boolean {
  return isImageAttachment(attachment) || isPdfAttachment(attachment)
}

export function pdfThumbnailUrl(url: string): string {
  return `${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`
}

export function miniatureLineWidths(seed: string): number[] {
  const source = seed.trim() || "attachment"
  let hash = 0
  for (const char of source) {
    hash = (hash * 31 + char.charCodeAt(0)) % 997
  }
  return [82, 64, 76, 52].map((base, index) => {
    const variance = (hash + index * 17) % 14
    return Math.max(42, Math.min(88, base - variance))
  })
}

export function attachmentExtensionLabel(attachment: RobinFileAttachment): string {
  if (isPdfAttachment(attachment)) return "PDF"
  const extension = attachment.fileName.split(".").pop()?.trim().toUpperCase()
  if (!extension || extension.length > 5) return "FILE"
  return extension
}

export function formatBytes(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  let value = sizeBytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`
}

export function documentKindLabel(
  kind: RobinFileAttachment["documentKind"],
): string {
  switch (kind) {
    case "job_description":
      return "Job description"
    case "manager_note":
      return "Manager note"
    case "resume":
      return "Resume"
    case "unknown":
    default:
      return "Attachment"
  }
}

export function previewErrorMessage(error: unknown): string {
  if (error instanceof AgentApiError && error.status === 404) {
    return "This attachment is no longer available."
  }
  return "Could not load a preview for this attachment."
}
